// Search every voice in the voice library (search_voices), not just the built-in list: words plus a few filters,
// ranked for narration (storyteller voices first, ad-style voices down when asked). Each result plays the voice's own
// preview clip (free: nothing is generated or added); "Use" picks it. A library voice is added to our voice account
// when it's first used, by the api.
import { useState, type FormEvent } from "react";
import { tapTool, useVoice } from "../actions";
import { toggleAudio } from "../audio";
import { useStore } from "../store";
import type { Artifact } from "../types";
import { Icon, Spinner } from "./bits";

export type VoiceSearchFor = "narrator" | "character" | "director";

const IDEAS: Record<VoiceSearchFor, string[]> = {
  narrator: ["Deep, calm storyteller, not like an ad", "Warm audiobook narrator", "Gravelly, characterful, older"],
  character: ["Gruff old sailor", "Wise grandmother", "Nervous young inventor"],
  director: ["Calm, deep, unhurried", "Warm British storyteller", "Friendly, relaxed, not salesy"],
};

/** `onUse` picks for a character (the caller saves it); without it, "Use" sets the narrator or the Director voice. */
export function VoiceSearch({ for: who, initial = "", onUse }: { for: VoiceSearchFor; initial?: string; onUse?: (v: { voiceId: string; name: string; description: string }) => Promise<unknown> }) {
  const [text, setText] = useState(initial);
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [accent, setAccent] = useState("");
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState<Artifact[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const playing = useStore((s) => s.playing);

  const search = async (query: string) => {
    if (!query.trim()) return;
    setText(query);
    setLoading(true);
    const args = { query: query.trim(), for: who, ...(gender ? { gender } : {}), ...(age ? { age } : {}), ...(accent ? { accent } : {}) };
    const r = await tapTool("search_voices", args, `searched the voice library for "${query.trim()}"`, { keepCards: true });
    setCards(r ? r.cards.filter((c) => c.voiceId).map((c) => ({ ...c, projectId: who === "narrator" ? "" : c.projectId })) : []);
    setLoading(false);
  };

  const use = async (c: Artifact) => {
    setBusy(c.voiceId ?? null);
    if (onUse) await onUse({ voiceId: c.voiceId!, name: c.voiceName || c.title, description: c.note ?? "" });
    else await useVoice({ ...c, voiceFor: who === "director" ? "director" : "narrator" });
    setBusy(null);
  };

  return (
    <div className="voice-search">
      <p className="field-label">Search every voice</p>
      <ul className="voice-asks" aria-label="Voice search ideas">
        {IDEAS[who].map((a) => (
          <li key={a}>
            <button data-action="voice.search" type="button" className={`chip${text === a ? " is-on" : ""}`} onClick={() => void search(a)} disabled={loading}>
              {a}
            </button>
          </li>
        ))}
      </ul>
      <form
        className="composer voice-ask"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void search(text);
        }}
      >
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Deep, calm storyteller, not like an ad" aria-label="Describe the voice to search for" enterKeyHint="search" />
        <button data-action="voice.search" type="submit" className="icon-btn composer-send" aria-label="Search the voice library" disabled={!text.trim() || loading}>
          <Icon name="search" />
        </button>
      </form>
      <div className="voice-filters">
        <label className="select">
          <select value={gender} onChange={(e) => setGender(e.target.value)} aria-label="Gender">
            <option value="">Any gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="neutral">Neutral</option>
          </select>
        </label>
        <label className="select">
          <select value={age} onChange={(e) => setAge(e.target.value)} aria-label="Age">
            <option value="">Any age</option>
            <option value="young">Young</option>
            <option value="middle_aged">Middle-aged</option>
            <option value="old">Old</option>
          </select>
        </label>
        <label className="select">
          <select value={accent} onChange={(e) => setAccent(e.target.value)} aria-label="Accent">
            <option value="">Any accent</option>
            {["american", "british", "australian", "irish", "scottish", "canadian", "indian"].map((x) => (
              <option key={x} value={x}>
                {x[0]!.toUpperCase() + x.slice(1)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {loading && <Spinner label="Searching the voice library" />}
      {!loading && cards && !cards.length && <p className="muted">Nothing came back. Try other words, or fewer filters.</p>}
      {!loading && !!cards?.length && (
        <ul className="vrows voice-search-results">
          {cards.map((c) => {
            const key = `vs-${who}-${c.voiceId}`;
            return (
              <li key={c.voiceId} className="vrow">
                <button data-action="voice.preview" type="button" className={`card-audio-btn${playing === key ? " is-on" : ""}`} onClick={() => toggleAudio(key, c.url)} disabled={!c.url} aria-label={c.url ? `Hear ${c.voiceName || c.title}'s preview` : "No preview for this voice"}>
                  <Icon name={playing === key ? "pause" : "play"} size={16} />
                </button>
                <span className="vrow-text">
                  <strong>{c.voiceName || c.title}</strong>
                  {c.note && <span className="muted">{c.note}</span>}
                </span>
                <button data-action={onUse ? "cast.voice" : "voice.use"} type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void use(c)}>
                  {busy === c.voiceId ? "Saving" : who === "narrator" ? "Use as narrator" : "Use"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!!cards?.length && <p className="field-hint">Previews are each voice's own short clip, free to play.</p>}
    </div>
  );
}
